import { injectPostgresEndpoints } from "./postgresApi";
import { withCompletionIdentity, type CompletionIdentity } from "./completionRequest";
import { Feature, type Lesson, type EntitlementsData } from "../../types/newtypes";

// Master Course Level types
export type LevelStatus =
  | "completed"
  | "in_progress"
  | "not_started"
  | "locked"
  | "locked_by_plan";

export interface MasterCourseLevel {
  id: string;
  title: string;
  levelNumber: number;
  estimatedDurationMinutes: number;
  rewardXp: number;
}

export interface LevelLesson {
  id: string;
  title: string;
  order: number;
  status: "completed" | "unlocked" | "locked";
  isOptional?: boolean;
}

export interface MasterCourseLevelWithProgress extends MasterCourseLevel {
  progressPercentage: number;
  isCompleted: boolean;
  status: LevelStatus;
  unlockMessage?: string;
  iconUri?: string;
  lessons?: LevelLesson[];  // Lessons may be included in v11 response
}

export interface CourseResource {
  key: string;
  title: string;
  description: string;
  kind: "pdf";
  downloadUrl: string;
  fileName: string;
}

// Master Course types
export interface MasterCourse {
  id: string;
  title: string;
  description: string | null;
  iconUri: string | null;
  certificatePolicy: string;
  totalLevels: number;
  estimatedTotalDurationMinutes: number;
  totalRewardXp: number;
  allowedTiers: string[];
  isPedagogueCertified: boolean;
  resources?: CourseResource[];
  levels: MasterCourseLevel[];
}

export interface MasterCourseWithProgress {
  id: string;
  title: string;
  description: string | null;
  iconUri: string | null;
  certificatePolicy: string;
  totalLevels: number;
  estimatedTotalDurationMinutes: number;
  totalRewardXp: number;
  allowedTiers: string[];
  isPedagogueCertified: boolean;
  resources?: CourseResource[];
  progressPercentage: number;
  completedLevels: number;
  isCompleted: boolean;
  levels: MasterCourseLevelWithProgress[];
}

// Level with lessons type (returned by /courses/{levelId} without v11)
export interface LevelWithLessons {
  id: string;
  title: string;
  description?: string | null;
  levelNumber?: number;
  reward_xp?: number;
  rewardXp?: number;
  estimated_total_duration_minutes?: number;
  estimatedTotalDurationMinutes?: number;
  progress_percentage?: number;
  progressPercentage?: number;
  is_completed?: boolean;
  isCompleted?: boolean;
  icon_uri?: string | null;
  iconUri?: string | null;
  lessons: Array<{
    id: string;
    title: string;
    order: number;
    status: "completed" | "unlocked" | "locked";
    isOptional?: boolean;
  }>;
  softLock?: {
    afterLessonOrder: number;
    message?: string | null;
  };
}

export interface LessonCompletionContext {
  master: MasterCourseWithProgress;
  level: LevelWithLessons;
  currentLevel: MasterCourseLevelWithProgress;
  certificateEntitled: boolean | null;
}

const uncertainContext = () => ({ error: {
  status: "CUSTOM_ERROR" as const,
  error: "Course progress could not be confirmed",
} });

export const masterCourseApi = injectPostgresEndpoints({
  endpoints: (builder) => ({
    // Explicit GET refresh: never let route state or a cached module establish whole-course success.
    refreshLessonCompletionContext: builder.mutation<LessonCompletionContext, { lessonId: string; identity: CompletionIdentity }>({
      queryFn: ({ lessonId, identity }, api) => withCompletionIdentity(identity, api, async (request) => {
        const lessonResult = await request(`/lessons/${lessonId}`);
        if (lessonResult.error) {return lessonResult;}
        const lesson = lessonResult.data as Lesson;
        if (lesson?.id !== lessonId || !lesson.course_id) {return uncertainContext();}
        const listResult = await request("/courses?version=v11");
        if (listResult.error) {return listResult;}
        if (!Array.isArray(listResult.data)) {return uncertainContext();}
        const matches = (listResult.data as MasterCourseWithProgress[]).filter((course) =>
          Array.isArray(course.levels) && course.levels.some((level) => level.id === lesson.course_id));
        if (matches.length !== 1) {return uncertainContext();}
        const [masterResult, levelResult, entitlementResult] = await Promise.all([
          request(`/courses/${matches[0].id}?version=v11`),
          request(`/course-levels/${lesson.course_id}`),
          request("/users/me/entitlements"),
        ]);
        if (masterResult.error) {return masterResult;}
        if (levelResult.error) {return levelResult;}
        const master = masterResult.data as MasterCourseWithProgress;
        const level = levelResult.data as LevelWithLessons;
        if (master?.id !== matches[0].id || typeof master.isCompleted !== "boolean" || !Array.isArray(master.levels) ||
          master.levels.length === 0 || level?.id !== lesson.course_id || !Array.isArray(level.lessons) ||
          !level.lessons.some((item) => item.id === lessonId)) {return uncertainContext();}
        const currentLevel = master.levels.find((item) => item.id === level.id);
        const accessible = master.levels.filter((item) => item.status !== "locked_by_plan");
        if (!currentLevel || typeof currentLevel.isCompleted !== "boolean" || accessible.length === 0 ||
          (master.isCompleted && accessible.some((item) => item.isCompleted !== true)) ||
          (currentLevel.isCompleted && level.lessons.some((item) => item.status !== "completed"))) {return uncertainContext();}
        const entitlements = entitlementResult.data as EntitlementsData | undefined;
        const certificateEntitled = !entitlementResult.error && Array.isArray(entitlements?.features)
          ? !entitlements!.isSubscriptionExpired && entitlements!.features.includes(Feature.COURSE_CERTIFICATES)
          : null;
        return { data: { master, level, currentLevel, certificateEntitled } };
      }),
    }),
    // Get all master courses with user progress (v11 API via /courses endpoint)
    getMasterCourses: builder.query<MasterCourseWithProgress[], void>({
      query: () => "/courses?version=v11",
      providesTags: ["MasterCourses"],
    }),

    // Get single master course with levels and progress (v11 API)
    getMasterCourseById: builder.query<MasterCourseWithProgress, string>({
      query: (id) => `/courses/${id}?version=v11`,
      providesTags: (result, error, id) => [
        { type: "MasterCourses", id },
      ],
    }),

    downloadCourseResource: builder.mutation<Blob, string>({
      query: (downloadUrl) => ({
        url: downloadUrl,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
    }),

    // Get single level with lessons (legacy /course-levels endpoint)
    getLevelById: builder.query<LevelWithLessons, string>({
      query: (levelId) => `/course-levels/${levelId}`,
      providesTags: (result, error, id) => [
        { type: "Courses", id },
      ],
    }),
  }),
});

export const {
  useRefreshLessonCompletionContextMutation,
  useGetMasterCoursesQuery,
  useGetMasterCourseByIdQuery,
  useGetLevelByIdQuery,
  useLazyGetLevelByIdQuery,
  useDownloadCourseResourceMutation,
} = masterCourseApi;
